import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import AcUnitOutlinedIcon from "@mui/icons-material/AcUnitOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import TimelapseOutlinedIcon from "@mui/icons-material/TimelapseOutlined";
import GppBadOutlinedIcon from "@mui/icons-material/GppBadOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { UNO_URL, applicationId } from "../../../env";
import {
  FREEZER_FROST_SHARE_LIVE,
  FREEZER_FROST_SHARE_LIST,
  FREEZER_FROST_SHARE_EXPORT,
} from "../../../apiClient/endpoints";
import FreezerFrostFilters from "./FreezerFrostFilters";

const REFRESH_MS = 20000;

const sessionStatusMeta = (sessionStatus) => {
  switch (sessionStatus) {
    case "in_progress":
    case "entry_open":
      return { key: "in_progress", label: "In Progress", title: "AA currently inside freezer", dot: "green-blink" };
    case "cumulative_limit_reached":
      return {
        key: "cumulative_limit_reached",
        label: "Cumulative Limit Reached",
        title: "Rolling window total reached the cumulative exposure limit",
        dot: "red-blink",
      };
    case "completed_cumulative":
      return {
        key: "completed_cumulative",
        label: "Completed",
        title: "Cumulative time exceeded",
        dot: "red-solid",
      };
    case "exceeded_no_exit":
    case "exit_missed":
    case "auto_closed_missing_exit":
      return {
        key: "exceeded_no_exit",
        label: "Exceeded Exposure Limit",
        title: "Entry scanned — no exit scan",
        dot: "red-blink",
      };
    case "exceeded_with_exit":
      return {
        key: "exceeded_with_exit",
        label: "Exceeded Exposure Limit",
        title: "Entry and exit scanned — time limit exceeded",
        dot: "red-solid-pulse",
      };
    case "non_compliance":
    case "closed_non_compliance":
      return {
        key: "non_compliance",
        label: "Non-Compliance",
        title: "Closed on entry non-compliance",
        dot: "amber-solid",
      };
    default:
      return { key: "completed", label: "Completed", title: "Session completed within limit", dot: "green-solid" };
  }
};

const SessionStatusIndicator = ({ sessionStatus, isOpenSession = false }) => {
  const meta = sessionStatusMeta(sessionStatus);
  const isLive = isOpenSession || meta.key === "in_progress";
  return (
    <div
      className={`ff-dashboard-v2__sess-status ff-dashboard-v2__sess-status--${meta.key}${isLive ? " is-live-inside" : ""}`}
      title={meta.title}
    >
      <span className={`ff-dashboard-v2__sess-dot ff-dashboard-v2__sess-dot--${meta.dot}`} aria-hidden>
        <span className="ff-dashboard-v2__sess-dot-core" />
        {meta.dot === "green-blink" ||
        meta.dot === "red-blink" ||
        meta.dot === "red-solid-pulse" ||
        meta.dot === "violet-solid-pulse" ? (
          <>
            <span className="ff-dashboard-v2__sess-dot-ping" />
            <span className="ff-dashboard-v2__sess-dot-ping ff-dashboard-v2__sess-dot-ping--delay" />
          </>
        ) : null}
      </span>
      <div className="ff-dashboard-v2__sess-status-text">
        <strong>
          {meta.label}
          {isLive ? <span className="ff-dashboard-v2__sess-live-tag">LIVE</span> : null}
        </strong>
        <small>{meta.title}</small>
      </div>
    </div>
  );
};

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  return `${Math.round(seconds / 60)} min`;
};

const displayExitTime = (row) => {
  if (
    row?.exitProcessComplete ||
    row?.sessionStatus === "completed" ||
    row?.sessionStatus === "exceeded_with_exit" ||
    row?.sessionStatus === "completed_cumulative"
  ) {
    return formatWhen(row.exitTime);
  }
  if (row?.rawStatus === "completed" || row?.status === "completed") return formatWhen(row.exitTime);
  return "—";
};

const isLiveInsideSession = (row) =>
  row?.isOpenSession ||
  row?.rawStatus === "entry_open" ||
  row?.sessionStatus === "in_progress" ||
  row?.sessionStatus === "entry_open";

const formatLiveElapsed = (entryTime, nowMs) => {
  if (!entryTime) return "—";
  const start = new Date(entryTime).getTime();
  if (Number.isNaN(start)) return "—";
  const totalSec = Math.max(0, Math.floor((nowMs - start) / 1000));
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (mins < 60) return `${mins}m ${String(secs).padStart(2, "0")}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hours}h ${remMins}m ${String(secs).padStart(2, "0")}s`;
};

const formatRemainingExposure = (entryTime, exposureLimitSeconds, nowMs) => {
  const limitSec = Number(exposureLimitSeconds);
  if (!entryTime || !Number.isFinite(limitSec) || limitSec <= 0) {
    return { text: "—", tone: "muted", reached: false };
  }
  const start = new Date(entryTime).getTime();
  if (Number.isNaN(start)) return { text: "—", tone: "muted", reached: false };
  const elapsed = Math.max(0, Math.floor((nowMs - start) / 1000));
  const remainingSec = limitSec - elapsed;
  if (remainingSec <= 0) return { text: "Exposure limit reached.", tone: "danger", reached: true };
  const mins = Math.floor(remainingSec / 60);
  const secs = remainingSec % 60;
  if (mins < 60) {
    return {
      text: `${mins}m ${String(secs).padStart(2, "0")}s remaining`,
      tone: mins < 5 ? "warn" : "ok",
      reached: false,
    };
  }
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return { text: `${hours}h ${remMins}m remaining`, tone: "ok", reached: false };
};

/**
 * Public view-only Freezer-Frost live board (TV / stakeholders).
 * No app shell, no edit actions — auto-refreshes.
 */
const FreezerFrostLiveView = () => {
  const { token } = useParams();
  const [apiUrl, setApiUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [data, setData] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [modalType, setModalType] = useState(""); // nc | exceeded | cumulative
  const [modalLoading, setModalLoading] = useState(false);
  const [modalList, setModalList] = useState([]);
  const [expandedNc, setExpandedNc] = useState(null);
  const [dates, setDates] = useState({ start: null, end: null });
  const [week, setWeek] = useState("");
  const [site, setSite] = useState("");
  const [aaAlias, setAaAlias] = useState("");
  const [filterOptions, setFilterOptions] = useState({ sites: [], aaAliases: [], weeks: [] });
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${UNO_URL}/api/application/configuration/${applicationId}`)
      .then((response) => {
        const cfg = response.data?.data?.data || {};
        const host = window.location.hostname;
        const useLocalApi = host === "localhost" || host === "127.0.0.1";
        // Share/live API is not on production yet — local TV preview must hit local backend.
        const resolvedApi = useLocalApi ? "http://localhost:4002" : cfg.API_URL || "";
        if (!cancelled) setApiUrl(resolvedApi);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load application configuration.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const buildFilterParams = useCallback(() => {
    const params = {};
    if (week) {
      params.week = week;
      params.year = filterOptions.year || new Date().getFullYear();
    } else if (dates.start && dates.end) {
      params.start = dates.start.toISOString();
      params.end = dates.end.toISOString();
    } else if (dates.start) {
      params.start = dates.start.toISOString();
      params.end = (dates.end || dates.start).toISOString();
    }
    if (site) params.site = site;
    if (aaAlias) params.aaAlias = aaAlias;
    return params;
  }, [week, dates.start, dates.end, site, aaAlias, filterOptions.year]);

  const loadLive = useCallback(async () => {
    if (!apiUrl || !token) return;
    try {
      const { data: body } = await axios.get(`${apiUrl}${FREEZER_FROST_SHARE_LIVE}/${token}/live`, {
        params: buildFilterParams(),
      });
      if (!body?.status) {
        setError(body?.message || "Unable to load live dashboard");
        setErrorCode(body?.code || "");
        setData(null);
        return;
      }
      setData(body.data);
      if (body.data?.filterOptions) setFilterOptions(body.data.filterOptions);
      setError("");
      setErrorCode("");
    } catch (err) {
      const code = err?.response?.data?.code || "";
      const message = err?.response?.data?.message || err.message || "Unable to load live dashboard";
      setError(message);
      setErrorCode(code);
      if (code === "SHARE_EXPIRED" || code === "SHARE_NOT_FOUND") setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, buildFilterParams]);

  useEffect(() => {
    if (!apiUrl) return;
    loadLive();
    const id = setInterval(loadLive, REFRESH_MS);
    return () => clearInterval(id);
  }, [apiUrl, loadLive]);

  useEffect(() => {
    const onDoc = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setExportOpen(false);
        setExportDone(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const clearFilters = () => {
    setDates({ start: null, end: null });
    setWeek("");
    setSite("");
    setAaAlias("");
  };

  const hasActiveFilters = Boolean(week || site || aaAlias || dates.start || dates.end);

  const downloadSessionsCsv = async () => {
    if (!apiUrl || !token || exporting) return;
    setExporting(true);
    setExportDone(false);
    try {
      const response = await axios.get(`${apiUrl}${FREEZER_FROST_SHARE_EXPORT}/${token}/export`, {
        params: buildFilterParams(),
        responseType: "blob",
      });
      const stamp = new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(response.data);
      link.download = `freezer-frost-live-report-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      setExportDone(true);
    } catch (err) {
      console.log(err);
    } finally {
      setExporting(false);
    }
  };

  const openShareList = async (listType, modalKey) => {
    if (!apiUrl || !token) return;
    setModalType(modalKey);
    setModalLoading(true);
    setModalList([]);
    setExpandedNc(null);
    try {
      const { data: body } = await axios.get(`${apiUrl}${FREEZER_FROST_SHARE_LIST}/${token}/list/${listType}`, {
        params: buildFilterParams(),
      });
      setModalList(Array.isArray(body?.data) ? body.data : []);
    } catch (err) {
      setModalList([]);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setModalType("");
    setModalList([]);
    setExpandedNc(null);
  };

  const cards = [
    {
      key: "visits",
      label: "Total Freezer Visits",
      value: data?.totalVisits ?? data?.totalUnits,
      icon: <AcUnitOutlinedIcon />,
      tone: "purple",
    },
    {
      key: "active",
      label: "AAs Active Inside Freezer",
      value: data?.activeSessions,
      icon: <SensorsOutlinedIcon />,
      tone: "live",
      live: true,
    },
    {
      key: "completed",
      label: "AAs Completed within exposure limit",
      value: data?.completedSessions,
      icon: <CheckCircleOutlineIcon />,
      tone: "green",
    },
    {
      key: "missed",
      label: "AAs Exceeded Exposure for single entry",
      value: data?.missedExit,
      icon: <TimerOffOutlinedIcon />,
      tone: "red",
      alertOnCount: true,
      viewAction: "exceeded",
    },
    {
      key: "cumulative",
      label: "Cumulative Limit Reached for single shift",
      value: data?.cumulativeLimitReached,
      icon: <HourglassTopOutlinedIcon />,
      tone: "red",
      alertOnCount: true,
      viewAction: "cumulative",
    },
    {
      key: "avg",
      label: "Average Operation Time",
      value: data?.avgDurationMinutes != null ? `${data.avgDurationMinutes} min` : "0 min",
      icon: <TimelapseOutlinedIcon />,
      tone: "purple",
    },
    {
      key: "nc",
      label: "Non-Compliances Reported During Freezer Entry",
      value: data?.nonComplianceSessions,
      icon: <GppBadOutlinedIcon />,
      tone: "red",
      viewAction: "nc",
    },
  ];

  const onViewClick = (viewAction) => {
    if (viewAction === "nc") openShareList("non-compliance", "nc");
    else if (viewAction === "exceeded") openShareList("exceeded-exposure", "exceeded");
    else if (viewAction === "cumulative") openShareList("cumulative-limit", "cumulative");
  };

  const iconTone = (tone) => {
    if (tone === "live") return "live";
    if (tone === "amber" || tone === "orange") return "amber";
    if (tone === "red") return "red";
    return tone;
  };

  if (error && !data) {
    return (
      <div className="portal-ui-v2 ff-live-board ff-live-board--error">
        <div className="ff-live-board__error-card">
          <span className="ff-live-board__live-pill is-off">OFFLINE</span>
          <h1>{errorCode === "SHARE_EXPIRED" ? "Link expired" : "Dashboard unavailable"}</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-ui-v2 ff-live-board">
      {/* Use div — global `header { position:fixed }` from style.css breaks this board */}
      <div className="ff-live-board__header" role="banner">
        <div className="ff-live-board__brand">
          <div className="ff-live-board__brand-mark">
            <AcUnitOutlinedIcon />
          </div>
          <div className="ff-live-board__brand-text">
            <p className="ff-live-board__eyebrow">Freezer-Frost · Live board</p>
            <h1>{loading ? <Skeleton width={180} /> : data?.groupName || "Freezer-Frost"}</h1>
            <p className="ff-live-board__sub">View-only · auto-updates every {REFRESH_MS / 1000}s</p>
          </div>
        </div>
        <div className="ff-live-board__meta">
          <div className="ff-live-board__toolbar">
            <FreezerFrostFilters
              dates={dates}
              setDates={setDates}
              week={week}
              setWeek={setWeek}
              site={site}
              setSite={setSite}
              aaAlias={aaAlias}
              setAaAlias={setAaAlias}
              filterOptions={filterOptions}
              onClear={clearFilters}
            />
            <div className="ff-dash-export" ref={exportRef}>
              <button
                type="button"
                className={`ff-dashboard-v2__share-btn ff-dash-export__trigger${exportOpen ? " is-open" : ""}`}
                onClick={() => {
                  setExportOpen((v) => !v);
                  setExportDone(false);
                }}
                aria-expanded={exportOpen}
                aria-haspopup="dialog"
              >
                <FileDownloadOutlinedIcon />
                Export report
                <KeyboardArrowDownIcon className="ff-dash-export__chevron" fontSize="small" />
              </button>
              {exportOpen ? (
                <div className="ff-dash-export__panel" role="dialog" aria-label="Download live dashboard report">
                  <div className="ff-dash-export__panel-head">
                    <div>
                      <strong>Download dashboard report</strong>
                      <p>Recent Freezer Sessions — full field export</p>
                    </div>
                    <button
                      type="button"
                      className="ff-dash-export__panel-close"
                      onClick={() => setExportOpen(false)}
                      aria-label="Close"
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>
                  <ul className="ff-dash-export__points">
                    <li>
                      <span className="ff-dash-export__point-icon" aria-hidden>
                        <DescriptionOutlinedIcon />
                      </span>
                      <div>
                        <strong>Freezer-Frost live report</strong>
                        <span>All sessions matching your current filters (date / week / site / AA alias).</span>
                      </div>
                    </li>
                    <li>
                      <span className="ff-dash-export__point-icon is-csv" aria-hidden>
                        <TableChartOutlinedIcon />
                      </span>
                      <div>
                        <strong>CSV file (.csv)</strong>
                        <span>Opens directly in Excel, Google Sheets, or Numbers.</span>
                      </div>
                    </li>
                  </ul>
                  <div className="ff-dash-export__meta">
                    <span className="ff-dash-export__badge">CSV</span>
                    <span>
                      {hasActiveFilters
                        ? "Uses active filters on this live board"
                        : "All sessions (no filters applied)"}
                    </span>
                  </div>
                  {exportDone ? (
                    <p className="ff-dash-export__done">Report downloaded — check your Downloads folder.</p>
                  ) : null}
                  <div className="ff-dash-export__foot">
                    <button type="button" className="ff-dash-export__cancel" onClick={() => setExportOpen(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ff-dash-export__primary"
                      onClick={downloadSessionsCsv}
                      disabled={exporting || loading}
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                      {exporting ? "Preparing CSV…" : "Download CSV report"}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <span className="ff-live-board__live-pill">
            <span className="ff-live-board__live-dot" />
            LIVE
          </span>
          <div className="ff-live-board__clock">
            <strong>{clock.toLocaleTimeString()}</strong>
            <small>{clock.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}</small>
          </div>
          {data?.expiresAt ? (
            <small className="ff-live-board__expires">
              Expires {new Date(data.expiresAt).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </small>
          ) : null}
        </div>
      </div>

      <div className="ff-live-board__layout">
        <aside className="ff-live-board__aside" aria-label="Summary KPIs">
          <p className="ff-live-board__aside-title">Live KPIs</p>
          <div
            className="ff-live-board__kpis"
            style={{ gridTemplateRows: `repeat(${cards.length}, minmax(0, 1fr))` }}
          >
            {cards.map((card) => {
              const numericValue = Number(card.value);
              const isAlert = !!card.alertOnCount && Number.isFinite(numericValue) && numericValue >= 1;
              const showPulse = !!card.live || isAlert;
              return (
              <article
                className={`ff-live-board__kpi${card.live ? " is-live" : ""}${isAlert ? " is-alert-red" : ""}${card.viewAction ? " has-view" : ""}`}
                key={card.key}
              >
                <div className={`ff-live-board__kpi-icon ff-live-board__kpi-icon--${iconTone(card.tone)}${isAlert ? " is-alert-pulse" : ""}`}>
                  {showPulse ? (
                    <span className={`ff-dashboard-v2__live-wrap${isAlert ? " is-alert" : ""}`} aria-hidden>
                      <span className="ff-dashboard-v2__live-ping" />
                      <span className="ff-dashboard-v2__live-ping ff-dashboard-v2__live-ping--delay" />
                      <span className="ff-dashboard-v2__live-dot" />
                      {card.icon}
                    </span>
                  ) : (
                    card.icon
                  )}
                </div>
                <div className="ff-live-board__kpi-body">
                  <div className="ff-live-board__kpi-value-row">
                    <strong>{loading ? <Skeleton width={36} /> : card.value ?? 0}</strong>
                    {card.live ? (
                      <span className="ff-dashboard-v2__live-badge">
                        <span className="ff-dashboard-v2__live-badge-dot" />
                        LIVE
                      </span>
                    ) : null}
                    {isAlert ? (
                      <span className="ff-dashboard-v2__alert-badge">
                        <span className="ff-dashboard-v2__alert-badge-dot" />
                        ALERT
                      </span>
                    ) : null}
                  </div>
                  <span>{card.label}</span>
                </div>
                {card.viewAction ? (
                  <button
                    type="button"
                    className="ff-live-board__kpi-view"
                    onClick={() => onViewClick(card.viewAction)}
                    disabled={loading}
                  >
                    View
                  </button>
                ) : (
                  <span className="ff-live-board__kpi-view-slot" aria-hidden="true" />
                )}
              </article>
              );
            })}
          </div>
        </aside>

        <section className="ff-live-board__sessions">
          <div className="ff-live-board__sessions-head">
            <div className="ff-live-board__sessions-title">
              <h2>
                Recent <span>Freezer Sessions</span>
              </h2>
              <p>Latest entry / exit activity · up to 25 sessions</p>
            </div>
            <span className="ff-live-board__refresh">
              Last refresh: {data?.refreshedAt ? new Date(data.refreshedAt).toLocaleTimeString() : "—"}
            </span>
          </div>

          <div className="ff-live-board__table-card">
            <div className="table-responsive ff-live-board__table-scroll">
              <table className="ff-live-board__table">
                <colgroup>
                  <col className="ff-live-board__col-alias" />
                  <col className="ff-live-board__col-freezer" />
                  <col className="ff-live-board__col-time" />
                  <col className="ff-live-board__col-time" />
                  <col className="ff-live-board__col-duration" />
                  <col className="ff-live-board__col-remaining" />
                  <col className="ff-live-board__col-status" />
                </colgroup>
                <thead>
                  <tr>
                    <th>AA Alias</th>
                    <th>Freezer</th>
                    <th>Entry Time</th>
                    <th>Exit Time</th>
                    <th className="is-center">Duration</th>
                    <th>Remaining</th>
                    <th>Session Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7}>
                        <Skeleton count={10} />
                      </td>
                    </tr>
                  ) : data?.recentSessions?.length ? (
                    data.recentSessions.map((row) => {
                      const live = isLiveInsideSession(row);
                      const remaining = live
                        ? row.sessionStatus === "cumulative_limit_reached"
                          ? { text: "Cumulative limit reached.", tone: "danger", reached: true }
                          : formatRemainingExposure(row.entryTime, row.exposureLimitSeconds, clock.getTime())
                        : { text: "—", tone: "muted", reached: false };
                      const durationAlert = live && (remaining.tone === "danger" || remaining.reached);
                      return (
                      <tr key={row.uuid}>
                        <td className="ff-live-board__cell-alias">{row.aaAlias || "—"}</td>
                        <td>
                          <div className="ff-live-board__freezer-cell">
                            <span className="ff-live-board__freezer-icon" aria-hidden>
                              <AcUnitOutlinedIcon fontSize="inherit" />
                            </span>
                            <strong>{row.freezerName || "—"}</strong>
                          </div>
                        </td>
                        <td className="ff-live-board__cell-time">{formatWhen(row.entryTime)}</td>
                        <td className="ff-live-board__cell-time">{displayExitTime(row)}</td>
                        <td className="is-center">
                          {live ? (
                            <span
                              className={`ff-live-board__duration is-live${durationAlert ? " is-alert" : ""}`}
                              title="Live exposure duration"
                            >
                              <span className="ff-dashboard-v2__duration-live-dot" aria-hidden />
                              {formatLiveElapsed(row.entryTime, clock.getTime())}
                            </span>
                          ) : (
                            <span className="ff-live-board__duration">{formatDuration(row.durationSeconds)}</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`ff-live-board__remaining is-${remaining.tone}${remaining.reached ? " is-reached" : ""}`}
                          >
                            {remaining.text}
                          </span>
                        </td>
                        <td className="ff-live-board__cell-status">
                          <SessionStatusIndicator
                            sessionStatus={row.sessionStatus || row.status}
                            isOpenSession={!!row.isOpenSession}
                          />
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <div className="ff-live-board__empty">
                          <strong>No freezer sessions yet</strong>
                          <span>Live activity will appear here as scans happen.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {modalType ? (
        <div className="ff-dashboard-v2__modal-overlay" onClick={closeModal}>
          <div
            className="ff-dashboard-v2__modal ff-live-board__modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ff-dashboard-v2__modal-head">
              <div>
                <h3>
                  {modalType === "nc"
                    ? "Non-Compliances — Freezer Entry"
                    : modalType === "exceeded"
                      ? "AAs Exceeded Exposure for single entry"
                      : "Cumulative Limit Reached for single shift"}
                </h3>
                <p>
                  {modalType === "nc"
                    ? "Each row is one closed session. Expand to see No answers."
                    : modalType === "exceeded"
                      ? "Sessions that exceeded the per-visit exposure / exit timeout."
                      : "Sessions where the AA Alias hit the rolling cumulative exposure limit."}
                </p>
              </div>
              <button type="button" className="ff-dashboard-v2__modal-close" onClick={closeModal} aria-label="Close">
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="ff-dashboard-v2__modal-body">
              {modalLoading ? (
                <Skeleton count={4} />
              ) : modalList.length === 0 ? (
                <p className="ff-dashboard-v2__empty">No sessions in this list right now.</p>
              ) : modalType === "nc" ? (
                <table className="sites-v2__table">
                  <thead>
                    <tr>
                      <th>AA Alias</th>
                      <th>Freezer</th>
                      <th>Entry</th>
                      <th>No Count</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {modalList.map((row) => (
                      <React.Fragment key={row.uuid}>
                        <tr>
                          <td>{row.aaAlias || "—"}</td>
                          <td>{row.freezerName || "—"}</td>
                          <td>{formatWhen(row.entryTime)}</td>
                          <td>{row.nonComplianceCount ?? 0}</td>
                          <td>Session closed</td>
                          <td>
                            <button
                              type="button"
                              className="ff-dashboard-v2__view-btn"
                              onClick={() => setExpandedNc(expandedNc === row.uuid ? null : row.uuid)}
                            >
                              {expandedNc === row.uuid ? "Hide Nos" : "Nos"}
                            </button>
                          </td>
                        </tr>
                        {expandedNc === row.uuid ? (
                          <tr className="ff-dashboard-v2__nc-detail-row">
                            <td colSpan={6}>
                              {row.noAnswers?.length ? (
                                <ul className="ff-dashboard-v2__no-list">
                                  {row.noAnswers.map((item, index) => (
                                    <li key={item.questionId || index}>
                                      <strong>No</strong> — {item.question || item.title || "Question"}
                                      {item.attachments?.length ? (
                                        <span className="ff-dashboard-v2__no-meta"> · {item.attachments.length} evidence</span>
                                      ) : null}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="ff-dashboard-v2__empty">
                                  {row.nonComplianceCount || 0} No response(s) recorded in this session.
                                </p>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="sites-v2__table">
                  <thead>
                    <tr>
                      <th>AA Alias</th>
                      <th>Freezer</th>
                      <th>Entry</th>
                      <th>Exit</th>
                      <th>Duration</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalList.map((row) => (
                      <tr key={row.uuid}>
                        <td>{row.aaAlias || "—"}</td>
                        <td>{row.freezerName || "—"}</td>
                        <td>{formatWhen(row.entryTime)}</td>
                        <td>{row.exitProcessComplete ? formatWhen(row.exitTime) : "—"}</td>
                        <td>
                          {row.isOpenSession
                            ? formatLiveElapsed(row.entryTime, clock.getTime())
                            : formatDuration(row.durationSeconds)}
                        </td>
                        <td>
                          <SessionStatusIndicator
                            sessionStatus={row.sessionStatus || row.status}
                            isOpenSession={!!row.isOpenSession}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="ff-live-board__footer">View-only · No edits · Machine Monitoring Freezer-Frost</div>
    </div>
  );
};

export default FreezerFrostLiveView;
